import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createMemberEnrollmentBodySchema,
  type CreateMemberEnrollmentBodyType,
} from '@zlinkx/shared-v2';
import { useTranslation } from '@/i18n';
import { i18nToast } from '@/utils/i18n-toast';
import type { MemberEnrollmentFormValues } from '@/types/member-enrollment.types';
import type { MerchantGroupCard } from '@/types/merchant-groups.types';

interface AddMemberModalProps {
  merchantId: number;
  groups: MerchantGroupCard[];
  isOpen: boolean;
  onClose: () => void;
  isSubmitting: boolean;
  onEnroll: (data: CreateMemberEnrollmentBodyType) => Promise<unknown>;
}

function createOperationId(): string {
  return globalThis.crypto.randomUUID();
}

function OpenAddMemberModal({
  groups,
  isOpen,
  onClose,
  isSubmitting,
  onEnroll,
}: AddMemberModalProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const [operationId, setOperationId] = useState(createOperationId);
  const [groupSource, setGroupSource] = useState<'existing' | 'new'>('existing');
  const lastSubmittedFingerprint = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<MemberEnrollmentFormValues>({
    resolver: zodResolver(createMemberEnrollmentBodySchema),
    shouldUnregister: true,
    defaultValues: {
      operation_id: operationId,
      phone_number: '',
      display_name: '',
      preferred_language: 'fr',
      enrollment_mode: 'personal',
      member_id: '',
      group_card_id: groups[0]?.card_id,
    },
  });
  const enrollmentMode = watch('enrollment_mode') ?? 'personal';
  const needsGroup = enrollmentMode === 'company' || enrollmentMode === 'both';
  const needsPersonal = enrollmentMode === 'personal' || enrollmentMode === 'both';

  useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
      return;
    }
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    const nextOperationId = createOperationId();
    setOperationId(nextOperationId);
    setGroupSource(groups.length > 0 ? 'existing' : 'new');
    lastSubmittedFingerprint.current = null;
    setSubmitError(null);
    reset({
      operation_id: nextOperationId,
      phone_number: '',
      display_name: '',
      preferred_language: 'fr',
      enrollment_mode: 'personal',
      member_id: '',
      group_card_id: groups[0]?.card_id,
      group_name: undefined,
      group_member_id: undefined,
    });
  }, [groups, isOpen, reset]);

  const onSubmit = async (values: MemberEnrollmentFormValues): Promise<void> => {
    const groupValues = needsGroup
      ? groupSource === 'existing'
        ? { group_card_id: values.group_card_id, group_name: undefined, group_member_id: undefined }
        : { group_card_id: undefined, group_name: values.group_name, group_member_id: values.group_member_id }
      : { group_card_id: undefined, group_name: undefined, group_member_id: undefined };
    const parsed = createMemberEnrollmentBodySchema.safeParse({
      ...values,
      ...groupValues,
      member_id: needsPersonal ? values.member_id : undefined,
      operation_id: operationId,
    });
    if (!parsed.success) {
      setSubmitError(t('common.errors.validation'));
      return;
    }

    const fingerprint = JSON.stringify({ ...parsed.data, operation_id: undefined });
    let nextOperationId = operationId;
    if (lastSubmittedFingerprint.current !== null && lastSubmittedFingerprint.current !== fingerprint) {
      nextOperationId = createOperationId();
      setOperationId(nextOperationId);
    }
    lastSubmittedFingerprint.current = fingerprint;
    setSubmitError(null);

    try {
      await onEnroll({ ...parsed.data, operation_id: nextOperationId });
      i18nToast.success('merchant.memberList.enrollment.success');
      onClose();
    } catch {
      setSubmitError(t('merchant.memberList.enrollment.retryableError'));
    }
  };

  const handleInvalidSubmit = (): void => {
    setSubmitError(t('common.errors.validation'));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-5">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t('merchant.memberList.enrollment.title')}</h2>
            <p className="mt-1 text-sm text-gray-600">{t('merchant.memberList.enrollment.description')}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-2 text-gray-500 hover:bg-gray-100" aria-label={t('merchant.analytics.close')}>
            {t('merchant.analytics.close')}
          </button>
        </div>
        <form className="space-y-4 p-5" onSubmit={event => void handleSubmit(onSubmit, handleInvalidSubmit)(event)}>
          <input type="hidden" {...register('operation_id')} />
          <div>
            <label htmlFor="enrollment-mode" className="mb-1 block text-sm font-medium text-gray-700">{t('merchant.memberList.enrollment.modeLabel')}</label>
            <select id="enrollment-mode" {...register('enrollment_mode')} className="w-full rounded-lg border border-gray-300 px-3 py-2">
              <option value="personal">{t('merchant.memberList.enrollment.modes.personal')}</option>
              <option value="company">{t('merchant.memberList.enrollment.modes.company')}</option>
              <option value="both">{t('merchant.memberList.enrollment.modes.both')}</option>
            </select>
          </div>
          <div>
            <label htmlFor="enrollment-phone" className="mb-1 block text-sm font-medium text-gray-700">{t('merchant.memberList.enrollment.phoneLabel')}</label>
            <input id="enrollment-phone" type="tel" autoComplete="tel" placeholder={t('merchant.memberList.enrollment.phonePlaceholder')} {...register('phone_number')} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
            {errors.phone_number !== undefined && <p className="mt-1 text-sm text-red-600">{t('merchant.memberList.enrollment.validation.phone')}</p>}
          </div>
          <div>
            <label htmlFor="enrollment-name" className="mb-1 block text-sm font-medium text-gray-700">{t('merchant.memberList.enrollment.nameLabel')}</label>
            <input id="enrollment-name" type="text" autoComplete="name" placeholder={t('merchant.memberList.enrollment.namePlaceholder')} {...register('display_name')} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
            {errors.display_name !== undefined && <p className="mt-1 text-sm text-red-600">{t('merchant.memberList.enrollment.validation.nameRequired')}</p>}
          </div>
          <div>
            <label htmlFor="enrollment-language" className="mb-1 block text-sm font-medium text-gray-700">{t('merchant.memberList.enrollment.languageLabel')}</label>
            <select id="enrollment-language" {...register('preferred_language')} className="w-full rounded-lg border border-gray-300 px-3 py-2">
              <option value="en">{t('merchant.memberList.enrollment.languages.en')}</option>
              <option value="fr">{t('merchant.memberList.enrollment.languages.fr')}</option>
              <option value="zh">{t('merchant.memberList.enrollment.languages.zh')}</option>
            </select>
          </div>
          {needsPersonal && (
            <div>
              <label htmlFor="enrollment-member-id" className="mb-1 block text-sm font-medium text-gray-700">{t('merchant.memberList.enrollment.memberIdLabel')}</label>
              <input id="enrollment-member-id" type="text" placeholder={t('merchant.memberList.enrollment.memberIdPlaceholder')} {...register('member_id')} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              {errors.member_id !== undefined && <p className="mt-1 text-sm text-red-600">{t('merchant.memberList.enrollment.validation.memberId')}</p>}
            </div>
          )}
          {needsGroup && (
            <div className="space-y-3 rounded-lg border border-gray-200 p-3">
              <p className="text-sm font-medium text-gray-700">{t('merchant.memberList.enrollment.groupTitle')}</p>
              <div className="flex gap-4 text-sm">
                <label><input type="radio" checked={groupSource === 'existing'} onChange={() => setGroupSource('existing')} /> {t('merchant.memberList.enrollment.groupExisting')}</label>
                <label><input type="radio" checked={groupSource === 'new'} onChange={() => setGroupSource('new')} /> {t('merchant.memberList.enrollment.groupNew')}</label>
              </div>
              {groupSource === 'existing' ? (
                <div>
                  <label htmlFor="enrollment-group-card" className="mb-1 block text-sm font-medium text-gray-700">{t('merchant.memberList.enrollment.groupSelectLabel')}</label>
                  <select id="enrollment-group-card" {...register('group_card_id')} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                    <option value="">{t('merchant.memberList.enrollment.groupSelectPlaceholder')}</option>
                    {groups.map(group => <option key={group.card_id} value={group.card_id}>{group.group_name} {group.member_id}</option>)}
                  </select>
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="enrollment-group-name" className="mb-1 block text-sm font-medium text-gray-700">{t('merchant.memberList.enrollment.groupNameLabel')}</label>
                    <input id="enrollment-group-name" type="text" {...register('group_name')} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                  </div>
                  <div>
                    <label htmlFor="enrollment-group-member-id" className="mb-1 block text-sm font-medium text-gray-700">{t('merchant.memberList.enrollment.groupMemberIdLabel')}</label>
                    <input id="enrollment-group-member-id" type="text" {...register('group_member_id')} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                  </div>
                </>
              )}
            </div>
          )}
          <p className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800">{t('merchant.memberList.enrollment.initializationHint')}</p>
          {submitError !== null && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{submitError}</p>}
          <div className="flex justify-end gap-3 border-t pt-4">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="rounded-lg border px-4 py-2 text-gray-700 hover:bg-gray-50">{t('merchant.memberList.enrollment.cancel')}</button>
            <button type="submit" disabled={isSubmitting} className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300">
              {isSubmitting ? t('merchant.memberList.enrollment.submitting') : t('merchant.memberList.enrollment.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AddMemberModal(props: AddMemberModalProps): React.JSX.Element | null {
  if (!props.isOpen) return null;
  return <OpenAddMemberModal {...props} />;
}
